import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiX, FiAlertTriangle } from 'react-icons/fi';
import riskService from '../../services/riskService';
import projectService from '../../services/projectService';
import './RiskForm.css';
import userService from '../../services/userService';

const RiskForm = ({ risk = null, onSubmit, onCancel }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    project_id: risk?.project_id || '',
    title: risk?.title || '',
    description: risk?.description || '',
    risk_type: risk?.risk_type || 'Technical',
    probability: risk?.probability || 'Medium',
    impact: risk?.impact || 'Medium',
    status: risk?.status || 'Identified',
    mitigation_plan: risk?.mitigation_plan || '',
    contingency_plan: risk?.contingency_plan || '',
    owner_id: risk?.owner_id || '',
    identified_date: risk?.identified_date ? new Date(risk.identified_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    review_date: risk?.review_date ? new Date(risk.review_date).toISOString().split('T')[0] : ''
  });

  const [calculatedScore, setCalculatedScore] = useState(0);
  const [calculatedSeverity, setCalculatedSeverity] = useState('Low');

  useEffect(() => {
    loadProjects();
    loadUsers();
  }, []);

  useEffect(() => {
    // Calculate risk score and severity when probability or impact changes
    if (formData.probability && formData.impact) {
      const score = riskService.calculateRiskScore(formData.probability, formData.impact);
      const severity = riskService.getSeverityLevel(score);
      setCalculatedScore(score);
      setCalculatedSeverity(severity);
    }
  }, [formData.probability, formData.impact]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAllProjects({ limit: 100 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadUsers = async () => {
    try {
      // Assuming there's a user service
      // For now, we'll leave it empty or you can implement it
      const data = await userService.getAll();

      setUsers(data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validation = riskService.validateRiskData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setLoading(true);
    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        if (risk) {
          await riskService.update(risk.id, formData);
        } else {
          await riskService.create(formData);
        }
        navigate('/app/risks');
      }
    } catch (error) {
      console.error('Error saving risk:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save risk. Please try again.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/app/risks');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="risk-form">
      <div className="risk-form-header">
        <h2 className="risk-form-title">
          {risk ? 'Edit Risk' : 'Create New Risk'}
        </h2>
        <div className="risk-form-score-display">
          <div className="risk-score-badge">
            <span className="risk-score-label">Risk Score:</span>
            <span className="risk-score-value">{calculatedScore}</span>
          </div>
          <div className={`risk-severity-badge ${riskService.getSeverityBadgeColor(calculatedSeverity)}`}>
            {calculatedSeverity} Severity
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Project */}
        <div className="form-group ">
          <label htmlFor="project_id">
            Project <span className="required">*</span>
          </label>
          <select
            id="project_id"
            name="project_id"
            value={formData.project_id}
            onChange={handleChange}
            className={`form-control ${errors.project_id ? 'error' : ''}`}
            required
          >
            <option value="">Select Project</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name} ({project.code})
              </option>
            ))}
          </select>
          {errors.project_id && <span className="error-message">{errors.project_id}</span>}
        </div>

        {/* Title */}
        <div className="form-group">
          <label htmlFor="title">
            Risk Title <span className="required">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={`form-control ${errors.title ? 'error' : ''}`}
            placeholder="Enter risk title"
            required
          />
          {errors.title && <span className="error-message">{errors.title}</span>}
        </div>
      </div>
      {/* Description */}
      <div className="form-group full-width">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="form-control"
          rows="4"
          placeholder="Describe the risk in detail"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Risk Type */}
        <div className="form-group">
          <label htmlFor="risk_type">
            Risk Type <span className="required">*</span>
          </label>
          <select
            id="risk_type"
            name="risk_type"
            value={formData.risk_type}
            onChange={handleChange}
            className={`form-control ${errors.risk_type ? 'error' : ''}`}
            required
          >
            {riskService.getRiskTypes().map(type => (
              <option key={type.value} value={type.value}>
                {type.icon} {type.label}
              </option>
            ))}
          </select>
          {errors.risk_type && <span className="error-message">{errors.risk_type}</span>}
        </div>

        {/* Status */}
        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="form-control"
          >
            {riskService.getStatusOptions().map(status => (
              <option key={status.value} value={status.value}>
                {status.icon} {status.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Probability */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <div className="form-group">
          <label htmlFor="probability">
            Probability <span className="required">*</span>
          </label>
          <select
            id="probability"
            name="probability"
            value={formData.probability}
            onChange={handleChange}
            className={`form-control ${errors.probability ? 'error' : ''}`}
            required
          >
            {riskService.getProbabilityLevels().map(level => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
          {errors.probability && <span className="error-message">{errors.probability}</span>}
        </div>

        {/* Impact */}
        <div className="form-group">
          <label htmlFor="impact">
            Impact <span className="required">*</span>
          </label>
          <select
            id="impact"
            name="impact"
            value={formData.impact}
            onChange={handleChange}
            className={`form-control ${errors.impact ? 'error' : ''}`}
            required
          >
            {riskService.getImpactLevels().map(level => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
          {errors.impact && <span className="error-message">{errors.impact}</span>}
        </div>

        {/* Owner */}
        <div className="form-group">
          <label htmlFor="owner_id">Risk Owner</label>
          <select
            id="owner_id"
            name="owner_id"
            value={formData.owner_id}
            onChange={handleChange}
            className="form-control"
          >
            <option value="">Unassigned</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.first_name} {user.last_name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Identified Date */}
        <div className="form-group">
          <label htmlFor="identified_date">Identified Date</label>
          <input
            type="date"
            id="identified_date"
            name="identified_date"
            value={formData.identified_date}
            onChange={handleChange}
            className="form-control"
          />
        </div>

        {/* Review Date */}
        <div className="form-group">
          <label htmlFor="review_date">Review Date</label>
          <input
            type="date"
            id="review_date"
            name="review_date"
            value={formData.review_date}
            onChange={handleChange}
            className="form-control"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

        {/* Mitigation Plan */}
        <div className="form-group">
          <label htmlFor="mitigation_plan">Mitigation Plan</label>
          <textarea
            id="mitigation_plan"
            name="mitigation_plan"
            value={formData.mitigation_plan}
            onChange={handleChange}
            className="form-control"
            rows="4"
            placeholder="Describe how to mitigate this risk"
          />
        </div>

        {/* Contingency Plan */}
        <div className="form-group">
          <label htmlFor="contingency_plan">Contingency Plan</label>
          <textarea
            id="contingency_plan"
            name="contingency_plan"
            value={formData.contingency_plan}
            onChange={handleChange}
            className="form-control"
            rows="4"
            placeholder="Describe the contingency plan if risk occurs"
          />
        </div>
      </div>


      {/* Risk Score Info */}
      <div className="risk-form-info">
        <FiAlertTriangle />
        <div>
          <strong>Risk Score Calculation:</strong> Probability ({formData.probability}) × Impact ({formData.impact}) = {calculatedScore}
          <br />
          <strong>Severity Level:</strong> {calculatedSeverity} (1-3: Low, 4-6: Medium, 7-9: High, 10-12: Critical)
        </div>
      </div>

      {/* Form Actions */}
      <div className="risk-form-actions">
        <button
          type="button"
          onClick={handleCancel}
          className="btn-secondary"
          disabled={loading}
        >
          <FiX />
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={loading}
        >
          <FiSave />
          {loading ? 'Saving...' : (risk ? 'Update Risk' : 'Create Risk')}
        </button>
      </div>
    </form>
  );
};

export default RiskForm;

